import { useState, useRef, useEffect } from 'react';
import MessageBubble from './MessageBubble';
import logo from '../logo2.png';

export default function ChatWindow({
  chat, onSendMessage, onStartChat, onStopGeneration, streaming, sidebarOpen, onToggleSidebar, onBack,
  models, modelsOffline, selectedModel, onSelectModel,
  ragAvailable, newChatUseRag, onToggleNewChatRag, onToggleChatRag,
  newChatUseWeb, onToggleNewChatWeb, onToggleChatWeb
}) {
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [headerDetailsOpen, setHeaderDetailsOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat?.messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    const message = input.trim();
    setInput('');
    setSending(true);
    try {
      if (!chat) {
        await onStartChat(selectedModel, message);
      } else {
        await onSendMessage(message);
      }
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  // Get display name for a model name
  const getDisplayName = (modelName) => {
    const m = models.find(x => x.name === modelName);
    return m?.displayName || modelName;
  };

  return (
    <div className="chat-window">
      <div className="chat-header">
        {!sidebarOpen && chat && (
          <button className="icon-btn back-btn" onClick={onBack} title="Back to chats">
            ←
          </button>
        )}
        {!sidebarOpen && (
          <button className="icon-btn menu-btn" onClick={onToggleSidebar}>
            ☰
          </button>
        )}
        <div className="chat-header-info">
          {chat ? (
            <>
              <h2 title={chat.title}>{chat.title}</h2>
              <button
                type="button"
                className={'chat-header-details-toggle' + (headerDetailsOpen ? ' open' : '')}
                onClick={() => setHeaderDetailsOpen(o => !o)}
                title={headerDetailsOpen ? 'Chat-Tools ausblenden' : 'Chat-Tools: Modell, RAG & Web-Suche'}
                aria-label="Chat-Tools"
                aria-expanded={headerDetailsOpen}
              >
                🛠️
                {(chat.useRag || chat.useWeb) && <span className="chat-header-details-dot" aria-hidden="true" />}
              </button>
              <div className={'chat-header-details' + (headerDetailsOpen ? ' open' : '')}>
                <span className="model-badge" title={getDisplayName(chat.model)}>{getDisplayName(chat.model)}</span>
                {ragAvailable && (
                  <button
                    type="button"
                    className="rag-toggle"
                    onClick={() => onToggleChatRag(!chat.useRag)}
                    title={chat.useRag ? 'RAG aktiv — klicken zum Deaktivieren' : 'RAG aus — klicken zum Aktivieren'}
                    style={{
                      border: '1px solid ' + (chat.useRag ? '#5af5c8' : '#333'),
                      background: chat.useRag ? '#0d1f1a' : 'transparent',
                      color: chat.useRag ? '#5af5c8' : '#888'
                    }}
                  >
                    📚 RAG <span className="toggle-state-label">{chat.useRag ? 'AN' : 'AUS'}</span>
                  </button>
                )}
                <button
                  type="button"
                  className="web-toggle"
                  onClick={() => onToggleChatWeb(!chat.useWeb)}
                  title={chat.useWeb ? 'Web-Suche aktiv — klicken zum Deaktivieren' : 'Web-Suche aus — klicken zum Aktivieren'}
                  style={{
                    border: '1px solid ' + (chat.useWeb ? '#5aa9f5' : '#333'),
                    background: chat.useWeb ? '#0d1622' : 'transparent',
                    color: chat.useWeb ? '#5aa9f5' : '#888'
                  }}
                >
                  🌐 Web <span className="toggle-state-label">{chat.useWeb ? 'AN' : 'AUS'}</span>
                </button>
              </div>
            </>
          ) : (
            <h2>NextVersion AI</h2>
          )}
        </div>
      </div>

      <div className="messages-container">
        {!chat ? (
          modelsOffline ? (
            <div className="welcome-screen">
              <div className="sleeping-emoji">😴</div>
              <div className="sleeping-zzz" aria-hidden="true">
                <span className="z z1">Z</span>
                <span className="z z2">z</span>
                <span className="z z3">z</span>
              </div>
              <h2>KI-Server schläft</h2>
              <p className="offline-msg">
                Der KI-Server ist aktuell nicht erreichbar.<br />
                Bitte wende dich an den Admin.
              </p>
            </div>
          ) : (
            <div className="welcome-screen">
              <div className="welcome-logo">
                <img src={logo} alt="NextVersion AI" style={{ width: '200px', objectFit: 'contain' }} />
              </div>
              <p>Choose a model and start chatting.</p>
              <div className="model-selector">
                <select
                  value={selectedModel}
                  onChange={e => onSelectModel(e.target.value)}
                  className="model-select"
                >
                  {models.map(m => (
                    <option key={m.name} value={m.name}>
                      {m.displayName || m.name}
                    </option>
                  ))}
                </select>
              </div>
              {ragAvailable && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: '#aaa', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!newChatUseRag}
                    onChange={onToggleNewChatRag}
                  />
                  📚 RAG nutzen (Antworten basierend auf hochgeladenen Dokumenten)
                </label>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: '#aaa', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!newChatUseWeb}
                  onChange={onToggleNewChatWeb}
                />
                🌐 Web-Suche nutzen (aktuelle Infos aus dem Internet, privat über SearXNG)
              </label>
            </div>
          )
        ) : chat.messages.length === 0 ? (
          <div className="welcome-screen">
            <div className="welcome-logo">
              <img src={logo} alt="NextVersion AI" style={{ width: '160px', objectFit: 'contain' }} />
            </div>
            <h2>New Conversation</h2>
            <p>Using <strong>{getDisplayName(chat.model)}</strong>. Type your message below to begin.</p>
          </div>
        ) : (
          <div className="messages-list">
            {chat.messages.map((msg, i) => (
              <MessageBubble key={i} message={msg} />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <form className="input-container" onSubmit={handleSubmit}>
        {streaming && (
          <button type="button" className="stop-btn" onClick={onStopGeneration}>
            <span style={{ fontSize: '18px', lineHeight: 1 }}>■</span> Stoppen
          </button>
        )}
        <div className="input-wrapper">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={!chat
              ? "Nachricht eingeben um neuen Chat zu starten..."
              : "Nachricht eingeben... (Ctrl+Enter zum Senden)"
            }
            rows={1}
            disabled={sending || modelsOffline}
          />
          <button
            type="submit"
            className="send-btn"
            disabled={!input.trim() || sending || modelsOffline}
          >
            {sending ? (
              <span className="sending-indicator">●●●</span>
            ) : (
              <span>➤</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
